// Partners Console — partner venue roster, verification, tiers, geography.
// Kit-based, cross-linked to Directory + the operational Partners editor.
import React, { useState, useEffect } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Bar, Donut, Table, Drawer, Empty, Pill, ExportButton, PALETTE, useConsoleTheme, useIsMobile } from '../components/Console';
import { supabase } from '../supabaseClient';
import { setFocus } from '../utils/consoleBus';

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());

function usePartners() {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_partners_overview').then(({ data }) => { if (!c) setD(data || {}); }); return () => { c = true; }; }, []);
  return d;
}

function Body({ onNav }) {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const d = usePartners();
  const [sel, setSel] = useState(null);
  const col = n => (mobile ? '1fr 1fr' : `repeat(${n},1fr)`);
  const empty = d && (d.total || 0) === 0;

  const types = (d && d.by_type) || [];
  const typeDonut = types.map((x, i) => ({ label: x.k, value: x.n, color: PALETTE[i % PALETTE.length] }));
  const tiers = (d && d.by_tier) || [];
  const maxTier = Math.max(1, ...tiers.map(x => x.n));
  const countries = (d && d.by_country) || [];
  const maxC = Math.max(1, ...countries.map(x => x.n));
  const rows = (d && d.rows) || [];

  const cols = [
    { label:'Partner', key:'name', render:r => r.name || '—' },
    { label:'Country', key:'country', render:r => r.country || '—' },
    { label:'Type', key:'type', muted:true, render:r => r.type || '—' },
    { label:'Tier', key:'tier', render:r => r.tier || '—' },
    { label:'Status', align:'right', render:r => <Pill tone={r.verified ? 'up' : 'warn'}>{r.verified ? 'verified' : (r.status || 'pending')}</Pill>, text:r => r.verified ? 'verified' : (r.status || 'pending') },
  ];

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns: col(4), gap:10 }}>
        <KPI label="Partners" value={fmt(d && d.total)} sub="total venues" accent="#6F8FA7" />
        <KPI label="Verified" value={fmt(d && d.verified)} sub={d && d.total ? `${Math.round((d.verified / Math.max(1, d.total)) * 100)}% of roster` : 'awaiting launch'} subColor={t.up} accent="#17a86a" />
        <KPI label="Pending" value={fmt(d && d.pending)} sub="need review" subColor={(d && d.pending) > 0 ? t.warn : t.mut} accent="#d97706" />
        <KPI label="Countries" value={fmt(d && d.countries)} sub="represented" accent={t.accent} />
      </div>

      {empty ? (
        <>
          <SectionLabel>Partner program</SectionLabel>
          <Panel>
            <Empty>No partners onboarded yet — this console arms automatically as venues join the program. Verification, tier mix, and geography fill in here. Use the editor to add the first partner.</Empty>
            <div style={{ marginTop:14 }}>
              <button onClick={() => onNav && onNav('partnerseditor')}
                style={{ fontSize:'0.9rem', padding:'8px 14px', borderRadius:8, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>
                ✎ Open Partners editor →
              </button>
            </div>
          </Panel>
        </>
      ) : (
        <>
          <SectionLabel right="click a country to compare">Mix & geography</SectionLabel>
          <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
            <Panel title="◔ By partner type">
              {!d ? <Empty>Loading…</Empty> : <Donut data={typeDonut} center={{ value: fmt(d.total), label: 'partners' }} />}
            </Panel>
            <Panel title="◷ Top countries">
              {countries.map(x => <Bar key={x.k} label={x.k} value={fmt(x.n)} pct={Math.round(x.n / maxC * 100)} labelWidth={130}
                onClick={() => { setFocus({ type:'country', value:x.k }); onNav && onNav('directory'); }}
                hint={`${x.k} — ${fmt(x.n)} · open in Directory`} />)}
            </Panel>
          </div>

          <SectionLabel>Tiers</SectionLabel>
          <Panel title="◇ Partner tiers">
            {tiers.length === 0 ? <Empty>No tier data yet.</Empty>
              : tiers.map((x, i) => <Bar key={x.k} label={x.k} value={fmt(x.n)} pct={Math.round(x.n / maxTier * 100)} color={PALETTE[i % PALETTE.length]} labelWidth={130} />)}
          </Panel>

          <SectionLabel right="click a row to inspect">Roster</SectionLabel>
          <Panel title="◈ Partner roster" right={<ExportButton filename="sanoire-partners" cols={cols} rows={rows} />}>
            <Table empty="No partners yet." cols={cols} rows={rows} onRowClick={r => setSel(r)} pageSize={20} />
          </Panel>

          <div style={{ marginTop:14 }}>
            <button onClick={() => onNav && onNav('partnerseditor')}
              style={{ fontSize:'0.9rem', padding:'8px 14px', borderRadius:8, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>
              ✎ Open Partners editor →
            </button>
          </div>
        </>
      )}

      <Drawer open={!!sel} onClose={() => setSel(null)} title="Partner">
        {sel && (
          <div>
            <div style={{ fontSize:'1.3rem', fontWeight:500, color:t.tx }}>{sel.name || '—'}</div>
            <div style={{ fontSize:'0.85rem', color:t.mut, marginBottom:12 }}>{[sel.city, sel.country].filter(Boolean).join(', ') || '—'}</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
              {sel.type && <Pill>{sel.type}</Pill>}
              {sel.tier && <Pill tone="up">{sel.tier}</Pill>}
              <Pill tone={sel.verified ? 'up' : 'warn'}>{sel.verified ? 'verified' : (sel.status || 'pending')}</Pill>
            </div>
            <button onClick={() => { setFocus({ type:'country', value:sel.country }); onNav && onNav('directory'); }}
              style={{ fontSize:'0.85rem', padding:'7px 12px', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontWeight:500 }}>
              {sel.country || 'Country'} in Directory →
            </button>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default function PartnersConsole(props) {
  return <ConsoleShell title="Partners Console"><Body onNav={props.onNav} /></ConsoleShell>;
}
